import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Shield, Plus, Trash2, X, Users, Edit,
  ChevronDown, ChevronUp, Lock
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RolesPermissions = () => {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [expandedRole, setExpandedRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    description: "",
    permissions: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { "Authorization": `Bearer ${token}` };

      const [rolesRes, permsRes] = await Promise.all([
        axios.get(`${API_URL}/api/roles`, { headers }),
        axios.get(`${API_URL}/api/permissions`, { headers })
      ]);

      if (rolesRes.data.success) {
        setRoles(rolesRes.data.data);
      }

      if (permsRes.data.success) {
        const filteredPerms = permsRes.data.data.permissions.filter(
          p => p.category !== "superadmin"
        );
        setPermissions(filteredPerms);
        
        const grouped = {};
        filteredPerms.forEach(perm => {
          if (!grouped[perm.category]) {
            grouped[perm.category] = [];
          }
          grouped[perm.category].push(perm);
        });
        setGroupedPermissions(grouped);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/roles`,
        formData,
        { headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        toast.success("Role berhasil dibuat");
        setShowCreateModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error("Error creating role:", error);
      toast.error(error.response?.data?.detail || "Gagal membuat role");
    }
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    
    let normalizedPermissions = [];
    if (role.permissions && Array.isArray(role.permissions)) {
      normalizedPermissions = role.permissions.map(perm => {
        if (typeof perm === "string") {
          return perm;
        } else if (typeof perm === "object" && perm !== null) {
          const resource = perm.resource || perm.name || "";
          const actions = perm.actions || [];
          if (actions.length > 0) {
            return actions.map(action => `${resource}.${action}`);
          }
          return perm.id || perm.name || "";
        }
        return "";
      }).flat().filter(Boolean);
    }
    
    setFormData({
      name: role.name,
      display_name: role.display_name,
      description: role.description || "",
      permissions: normalizedPermissions
    });
    setShowCreateModal(true);
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL}/api/roles/${editingRole.id}`,
        { permissions: formData.permissions },
        { headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        toast.success("Role berhasil diupdate");
        setShowCreateModal(false);
        setEditingRole(null);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error(error.response?.data?.detail || "Gagal update role");
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Hapus role "${roleName}"?`)) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `${API_URL}/api/roles/${roleId}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Role berhasil dihapus");
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error(error.response?.data?.detail || "Gagal menghapus role");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", display_name: "", description: "", permissions: [] });
    setEditingRole(null);
  };

  const togglePermission = (permId) => {
    const current = formData.permissions;
    if (current.includes(permId)) {
      setFormData({ ...formData, permissions: current.filter(p => p !== permId) });
    } else {
      setFormData({ ...formData, permissions: [...current, permId] });
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      dashboard: "bg-blue-100 text-blue-800",
      customers: "bg-green-100 text-green-800",
      billing: "bg-yellow-100 text-yellow-800",
      devices: "bg-purple-100 text-purple-800",
      users: "bg-indigo-100 text-indigo-800",
      roles: "bg-pink-100 text-pink-800",
      reports: "bg-orange-100 text-orange-800",
      monitoring: "bg-teal-100 text-teal-800",
      vpn: "bg-cyan-100 text-cyan-800",
      company: "bg-gray-100 text-gray-800"
    };
    return colors[category] || "bg-gray-100 text-gray-800";
  };

  const systemRoles = roles.filter(r => r.is_system_role);
  const customRoles = roles.filter(r => !r.is_system_role);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Roles & Permissions</h1>
          <p className="text-sm text-gray-600 mt-1 dark:text-slate-300">Kelola roles untuk staff internal Anda</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" />
          Tambah Role
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-slate-400">Loading roles...</div>
      ) : (
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-gray-600 dark:text-slate-300" />
              <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-200">System Roles</h2>
              <Badge variant="outline" className="text-xs">Bisa Edit Permissions</Badge>
            </div>
            <div className="grid gap-4">
              {systemRoles.map((role) => (
                <Card key={role.id} className="border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-900">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Shield className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                          <h3 className="text-lg font-bold text-gray-800 dark:text-slate-200">{role.display_name}</h3>
                          <Badge variant="outline" className="text-xs bg-white dark:bg-slate-800">System Role</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-3 dark:text-slate-300">{role.description}</p>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
                            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 dark:text-slate-300"
                          >
                            <Users className="w-4 h-4" />
                            {role.permissions?.length || 0} Permissions
                            {expandedRole === role.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>

                        {expandedRole === role.id && (
                          <div className="mt-4 p-4 bg-white rounded-lg border dark:bg-slate-800">
                            <p className="text-xs font-medium text-gray-700 mb-2 dark:text-slate-200">Permissions:</p>
                            <div className="flex flex-wrap gap-2">
                              {role.permissions?.map((permId) => {
                                const perm = permissions.find(p => p.id === permId);
                                return perm ? (
                                  <Badge key={permId} className={`text-xs ${getCategoryColor(perm.category)}`}>
                                    {perm.display_name}
                                  </Badge>
                                ) : null;
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditRole(role)}
                          className="p-2 text-indigo-500 hover:bg-indigo-50 rounded dark:hover:bg-indigo-900/30"
                          title="Edit Permissions"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-200">Custom Roles</h2>
              <Badge className="text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40">
                {customRoles.length} role{customRoles.length !== 1 ? "s" : ""}
              </Badge>
            </div>
            
            {customRoles.length === 0 ? (
              <Card className="border-dashed border-2">
                <CardContent className="pt-6 text-center py-12">
                  <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-2 dark:text-slate-400">Belum ada custom role</p>
                  <p className="text-sm text-gray-400 mb-4">
                    Buat custom role untuk kebutuhan internal staff Anda
                  </p>
                  <Button onClick={() => { resetForm(); setShowCreateModal(true); }} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Buat Custom Role
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {customRoles.map((role) => (
                  <Card key={role.id} className="hover:shadow-md transition-shadow border-indigo-200">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100">{role.display_name}</h3>
                            <Badge className="text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40">Custom</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-3 dark:text-slate-300">{role.description}</p>
                          
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}
                              className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                            >
                              <Users className="w-4 h-4" />
                              {role.permissions?.length || 0} Permissions
                              {expandedRole === role.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>

                          {expandedRole === role.id && (
                            <div className="mt-4 p-4 bg-indigo-50 rounded-lg dark:bg-indigo-900/30">
                              <p className="text-xs font-medium text-gray-700 mb-2 dark:text-slate-200">Permissions:</p>
                              <div className="flex flex-wrap gap-2">
                                {role.permissions?.map((permId) => {
                                  const perm = permissions.find(p => p.id === permId);
                                  return perm ? (
                                    <Badge key={permId} className={`text-xs ${getCategoryColor(perm.category)}`}>
                                      {perm.display_name}
                                    </Badge>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditRole(role)}
                            className="p-2 text-indigo-500 hover:bg-indigo-50 rounded dark:hover:bg-indigo-900/30"
                            title="Edit Role"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRole(role.id, role.display_name)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded dark:hover:bg-red-900/30"
                            title="Delete Role"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{editingRole ? "Edit Role" : "Tambah Role Baru"}</h2>
                <p className="text-sm text-indigo-100 mt-1">
                  {editingRole ? `Editing: ${editingRole.display_name}` : "Buat role baru untuk staff Anda"}
                </p>
              </div>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-white hover:bg-white/20 rounded-full p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={editingRole ? handleUpdateRole : handleCreateRole} className="p-6 space-y-6">
              {!editingRole && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Nama Role *</label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., supervisor_area"
                      />
                      <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Gunakan underscore, huruf kecil</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Display Name *</label>
                      <Input
                        required
                        value={formData.display_name}
                        onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                        placeholder="e.g., Supervisor Area"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">Deskripsi</label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                      rows="2"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Deskripsi role..."
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 dark:text-slate-200">
                  {editingRole ? "Edit Permissions" : "Pilih Permissions *"}
                </label>
                <p className="text-xs text-gray-500 mb-3 dark:text-slate-400">Pilih permissions yang sesuai untuk role ini</p>
                <div className="space-y-4 max-h-96 overflow-y-auto border rounded-lg p-4 bg-gray-50 dark:bg-slate-900">
                  {Object.entries(groupedPermissions).map(([category, perms]) => (
                    <div key={category} className="space-y-2">
                      <p className="font-medium text-gray-900 capitalize flex items-center gap-2 dark:text-slate-100">
                        <span className={`w-2 h-2 rounded-full ${getCategoryColor(category).split(" ")[0]}`}></span>
                        {category}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {perms.map((perm) => (
                          <label key={perm.id} className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer border border-transparent hover:border-indigo-200">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(perm.id)}
                              onChange={() => togglePermission(perm.id)}
                              className="rounded border-gray-300 dark:border-slate-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-slate-200">{perm.display_name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {formData.permissions.length > 0 && (
                  <p className="text-xs text-indigo-600 mt-2 dark:text-indigo-400">
                    ✓ {formData.permissions.length} permission{formData.permissions.length !== 1 ? "s" : ""} dipilih
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="button" onClick={() => { setShowCreateModal(false); resetForm(); }} variant="outline" className="flex-1">
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  disabled={formData.permissions.length === 0}
                >
                  {editingRole ? "Update Role" : "Buat Role"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPermissions;
